import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { CompanyControllerService, PersonDTO } from '@iota/iota-management-portal-v4-client';
import { catchError, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
    DeletePerson,
    LoadPersonsByCompany,
    PersonActionTypes,
    ReloadPerson,
    RemovePersonAddress,
    RemovePersonEmail,
    RemovePersonNote,
    RemovePersonPhoneNumber,
    UpsertPerson,
    UpsertPersonAddress,
    UpsertPersonEmail,
    UpsertPersonNote,
    UpsertPersonPhoneNumber
} from '../actions/person.actions';
import { selectedCompanyId, selectedRegulatorId } from '../store';
import { getLocationHeader } from 'src/app/shared/utils';
import { CompanyMode } from '../../shared/constants-and-enums/company-mode.enum';

@Injectable()
export class PersonEffects {
    loadPersonsByCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadPersonsByCompany),
            mergeMap(({ companyId }) =>
                this.apiService.getAllPersonsByCompany(companyId).pipe(
                    take(1),
                    map((persons) => ({
                        type: PersonActionTypes.LoadPersonsSuccess,
                        persons,
                        companyId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.LoadPersonsFailure, error: error }))
                )
            )
        )
    );

    reloadPerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReloadPerson),
            mergeMap(({ personId }) =>
                this.apiService.getAllPersonsByIds([personId]).pipe(
                    take(1),
                    map((persons) => ({
                        type: PersonActionTypes.ReloadPersonSuccess,
                        person: persons?.[0]
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    upsertPerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPerson),
            withLatestFrom(this.store.select(selectedCompanyId), this.store.select(selectedRegulatorId)),
            mergeMap(([{ person, mode }, companyId, regulatorId]) => {
                const personObj: PersonDTO = {
                    ...person,
                    ...(mode === CompanyMode.REGULATOR ? { centralRegulatorId: regulatorId } : { companyId: companyId })
                };

                return this.apiService.upsertPerson(personObj, 'response').pipe(
                    take(1),
                    map((response) => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId: getLocationHeader(response)
                    })),
                    catchError((error) =>
                        of({
                            type: PersonActionTypes.PersonActionFailure,
                            error
                        })
                    )
                );
            })
        )
    );

    deletePerson$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeletePerson),
            mergeMap(({ personId }) =>
                this.apiService.deletePerson(personId).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.DeletePersonSuccess,
                        personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.DeletePersonFailure, error: error }))
                )
            )
        )
    );

    upsertPersonEmail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPersonEmail),
            mergeMap(({ personId, email }) =>
                this.apiService.upsertPersonEmail(personId, email).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    removePersonEmail$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemovePersonEmail),
            mergeMap(({ emailId, personId }) =>
                this.apiService.removePersonEmail(emailId).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId: personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    upsertPersonPhoneNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPersonPhoneNumber),
            mergeMap(({ personId, phoneNumber }) =>
                this.apiService.upsertPersonPhoneNumber(personId, phoneNumber).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    removePersonPhoneNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemovePersonPhoneNumber),
            mergeMap(({ phoneNumberId, personId }) =>
                this.apiService.removePersonPhoneNumber(phoneNumberId).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId: personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    upsertPersonAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPersonAddress),
            mergeMap(({ personId, address }) =>
                this.apiService.upsertPersonAddress(personId, address).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    removePersonAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemovePersonAddress),
            mergeMap(({ addressId, personId }) =>
                this.apiService.removePersonAddress(addressId).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId: personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    upsertPersonNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertPersonNote),
            mergeMap(({ note, personId }) =>
                this.apiService.upsertPersonNote(personId, note).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    removePersonNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemovePersonNote),
            mergeMap(({ noteId, personId }) =>
                this.apiService.removePersonNote(noteId).pipe(
                    take(1),
                    map(() => ({
                        type: PersonActionTypes.ReloadPerson,
                        personId: personId
                    })),
                    catchError((error) => of({ type: PersonActionTypes.PersonActionFailure, error: error }))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private apiService: CompanyControllerService
    ) {}
}
